import { useCallback, useState } from 'react'
import { ApiError } from '../lib/api'
import { TARGET_LIFECYCLE, TARGET_SITUATION, useCommonCodes, useDongs, useLimits } from '../lib/codes'
import {
  CONSENTS,
  formatPhone,
  isEmailValid,
  isPhoneValid,
  type ConsentKey,
} from '../lib/citizenContact'
import {
  DESIRED_PERIODS,
  EMPTY_ADDRESS,
  IMPACT_SCOPES,
  RAW_TEXT_MIN,
  TARGET_MAX,
  UNKNOWN_FIELD,
  URGENCIES,
  isSubmittable,
  type PickedAddress,
  type ProblemFormValue,
} from '../lib/problemForm'
import {
  addressMetaText,
  useKakaoAddressSearch,
  type AddressCandidate,
  type KakaoAddressSearch,
} from '../lib/kakaoAddressSearch'
import { KakaoPointMap } from './KakaoPointMap'

/**
 * 시민문제 입력 폼 — **등록과 수정이 같은 화면을 쓴다**(09-04 합의). 수정은 별도 페이지를
 * 만들지 않고 이 폼을 `initial`로 채워서 연다.
 *
 * **묻는 축은 `mode`가 가른다**(09-07 사용자 확정 — 정본은 FE다): `new`는 **FE 등록 폼과
 * 같은 축**(원문·대상·주소·분야·연락처·동의)만 그리고, `edit`은 서버 `GET/PUT …/edit`이
 * 주고받는 넓은 축까지 그린다. **등록에 칸을 더하려면 FE부터 늘린다** — 여기만 넓히면
 * 「시민이 답한 값」의 분포를 잘못 읽게 된다(그 오진이 실제로 있었다 ·
 * `docs/10_설계/매칭-점수척도-진단과-처방.md` 2차 개정).
 *
 * **수정은 부분 갱신이다**(09-04에 「전체 교체」에서 뒤집었다) — 안 보낸 축은 서버가 지키고
 * 지우는 것은 `null` 명시다. 그 명시는 `toEditPayload`가 하고, 화면은 **등록 당시 값이
 * 채워진 채로** 열려야 시민이 비운 칸과 원래 빈 칸이 갈린다(채우는 책임은 서버
 * `GET …/edit`와 이 `initial`에 있다).
 *
 * **이름·연락처·동의를 받는다**(08-21 C-8 — 옛 설계서 C-01의 「1단계 개인정보 0개」를
 * 뒤집었다). 이름은 필수, 닿을 수단은 휴대폰·이메일 택1, 동의는 `privacy`·`disclose` 둘 다다.
 *
 * **주소는 카카오로 찾는다**(2026-08-25 결정) — 검색 결과가 행정동코드(`h_code`)를 함께
 * 주므로 행정동을 추정하지 않는다. 다만 **행정동 드롭다운을 지우지 않고 폴백으로 남긴다**:
 * 카카오 키가 없는 환경에서 검색이 죽으면 등록 경로가 통째로 닫히기 때문이다.
 *
 * **"잘 모르겠어요"는 빈칸과 다르다**(설계서 §4-4, 요건정리 §8-4).
 * 체크하면 `unknownFields`로 보내 `value_status='unknown'`으로 남는다 — 0점도 탈락도 아니다.
 * **수정 화면에만 있다** — FE 등록 폼에 그 토글이 없다.
 *
 * **실물 시민 화면은 `haebom-ai-platform-fe`이고 그쪽이 정본이다.** 이 화면은 서버 계약을
 * 같은 축으로 붙들어 두는 자리이지 시민이 본 것의 근거가 아니다.
 */

function toggle<T extends string>(list: T[], code: T, max?: number): T[] {
  if (list.includes(code)) return list.filter((c) => c !== code)
  if (max !== undefined && list.length >= max) return list
  return [...list, code]
}

const REQUIRED_CONSENTS: readonly ConsentKey[] = CONSENTS.map((c) => c.key)

export function ProblemCardForm({
  mode,
  initial,
  submitLabel,
  pendingLabel,
  isPending,
  error,
  onSubmit,
}: {
  /** `new` = FE 등록 폼과 같은 축만 · `edit` = 서버 수정 계약의 넓은 축까지(머리말). */
  mode: 'new' | 'edit'
  initial: ProblemFormValue
  submitLabel: string
  pendingLabel: string
  isPending: boolean
  error: unknown
  onSubmit: (value: ProblemFormValue) => void
}) {
  /** 등록에 없는 축 — 화면과 본문이 한 값에서 갈려야 「칸은 있는데 안 보내는」 어긋남이 없다. */
  const isEdit = mode === 'edit'
  const categories = useCommonCodes('category')
  // 대상은 두 축으로 나눠 묻는다(설계서 C-02) — 저장은 한 배열이다.
  const lifecycleTargets = useCommonCodes(TARGET_LIFECYCLE)
  const situationTargets = useCommonCodes(TARGET_SITUATION)
  const resources = useCommonCodes('resource')
  const dongs = useDongs()
  const limits = useLimits()

  const search = useKakaoAddressSearch()
  const [v, setV] = useState<ProblemFormValue>(initial)

  const patch = useCallback((next: Partial<ProblemFormValue>) => {
    setV((prev) => ({ ...prev, ...next }))
  }, [])

  const isUnknown = (field: string) => v.unknown.includes(field)
  const toggleUnknown = (field: string) =>
    setV((prev) => ({ ...prev, unknown: toggle(prev.unknown, field) }))

  /** 우리 행정동 목록에 있는 코드만 받는다 — 없으면 광명시 밖이다. */
  const pickAddress = useCallback(
    async (c: AddressCandidate) => {
      // 장소·동 이름으로 찾은 결과는 행정동코드가 비어 온다 — 좌표로 되물어 채운다.
      const resolved = await search.resolve(c)
      const known = (dongs.data ?? []).find((d) => d.code === resolved.regionCode)
      patch({
        address: {
          text: resolved.text,
          regionCode: known?.code ?? '',
          // 이름도 우리 목록의 것을 쓴다 — 표기가 흔들리면 안내 문구가 서로 달라진다.
          matchedName: known?.name ?? resolved.regionName,
          legalCode: resolved.legalCode,
          legalName: resolved.legalName,
          postalCode: resolved.postalCode ?? '',
          // 좌표는 지도에만 쓴다 — 본문에 싣지 않는다.
          lat: resolved.lat,
          lng: resolved.lng,
        },
      })
      search.clear()
    },
    [dongs.data, patch, search],
  )

  /** 폴백 — 검색을 못 쓸 때 동만 고른다. 주소 글자·좌표는 없다. */
  const selectDong = useCallback(
    (code: string) => {
      const name = (dongs.data ?? []).find((d) => d.code === code)?.name ?? ''
      patch({ address: { ...EMPTY_ADDRESS, regionCode: code, matchedName: name } })
    },
    [dongs.data, patch],
  )

  const contactValue = v.contactChannel === 'phone' ? v.contactPhone : v.contactEmail
  const hasContactError =
    v.contactChannel === 'phone' ? !isPhoneValid(v.contactPhone) : !isEmailValid(v.contactEmail)

  const canSubmit = isSubmittable(v, REQUIRED_CONSENTS) && !hasContactError && !isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit(v)
  }

  return (
    <form className="panel" onSubmit={handleSubmit}>
      <div className="field">
        <label htmlFor="rawText">
          어떤 불편이 있나요? <span className="req">*</span>
        </label>
        {/*
          상한 2000은 스키마(4000)가 아니라 **게이트 통과 구간**이다 — 2000을 넘으면 422
          `too_long`이라 다 적은 뒤에 반려된다. 하한 30도 같은 이유로 버튼에 걸어 둔다.
        */}
        <p className="hint">
          한 문단이면 충분합니다({RAW_TEXT_MIN}자 이상). 적어 주신 문장은 그대로 보관됩니다.
        </p>
        <textarea
          id="rawText"
          value={v.rawText}
          onChange={(e) => patch({ rawText: e.target.value })}
          maxLength={2000}
          placeholder="예) 아파트 앞 언덕이 가팔라서 유모차를 밀고 올라가기가 힘듭니다."
          required
        />
      </div>

      <div className="field">
        <label>
          누구의 문제인가요? <span className="req">*</span>
        </label>
        <p className="hint">
          연령대와 상황을 나눠 여쭙습니다. 둘 중 하나만 골라도 되고, 합쳐서 최대 {TARGET_MAX}개까지
          고를 수 있습니다.
        </p>
        <p className="hint">연령대</p>
        <div className="chips">
          {lifecycleTargets.data?.map((t) => (
            <button
              key={t.code}
              type="button"
              className={v.targetCodes.includes(t.code) ? 'opt on' : 'opt'}
              onClick={() =>
                setV((p) => ({ ...p, targetCodes: toggle(p.targetCodes, t.code, TARGET_MAX) }))
              }
              aria-pressed={v.targetCodes.includes(t.code)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="hint" style={{ marginTop: 12 }}>
          상황·특성
        </p>
        <div className="chips">
          {situationTargets.data?.map((t) => (
            <button
              key={t.code}
              type="button"
              className={v.targetCodes.includes(t.code) ? 'opt on' : 'opt'}
              onClick={() =>
                setV((p) => ({ ...p, targetCodes: toggle(p.targetCodes, t.code, TARGET_MAX) }))
              }
              aria-pressed={v.targetCodes.includes(t.code)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <AddressField
        address={v.address}
        regions={dongs.data ?? []}
        search={search}
        onPick={pickAddress}
        onSelectDong={selectDong}
      />

      {/* 시급도는 등록에서 안 묻는다 — FE에 칸이 없고 등록 스키마가 `mid`로 채운다. */}
      {isEdit ? (
        <div className="field">
          <label>얼마나 급한가요?</label>
          <div className="chips">
            {URGENCIES.map((u) => (
              <button
                key={u.value}
                type="button"
                className={v.urgency === u.value ? 'opt on' : 'opt'}
                onClick={() => patch({ urgency: u.value })}
                aria-pressed={v.urgency === u.value}
              >
                {u.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="field">
        <label>어떤 분야인가요?</label>
        <p className="hint">가장 가까운 것을 최대 {limits.categoryMax}개까지 고를 수 있습니다.</p>
        {/* 상한은 관리자 설정에서 온다(030). 초기값 2는 08-21 이티랩 확정
            (시민카드 docx Q1, 프로토타입 citizen-form-v2) — 안내 문구와 실제 상한이
            한 값에서 나와야 "2개라더니 3개가 눌리는" 어긋남이 안 생긴다. */}
        <div className="chips" aria-disabled={isUnknown(UNKNOWN_FIELD.category)}>
          {categories.data?.map((c) => (
            <button
              key={c.code}
              type="button"
              className={v.categoryCodes.includes(c.code) ? 'opt on' : 'opt'}
              onClick={() =>
                setV((p) => ({
                  ...p,
                  categoryCodes: toggle(p.categoryCodes, c.code, limits.categoryMax),
                }))
              }
              disabled={isUnknown(UNKNOWN_FIELD.category)}
              aria-pressed={v.categoryCodes.includes(c.code)}
            >
              {c.label}
            </button>
          ))}
        </div>
        {/* 「잘 모르겠어요」도 등록엔 없다 — FE 분야 단계는 고르거나 넘기거나 둘뿐이다. */}
        {isEdit ? (
          <label className="unknown-toggle">
            <input
              type="checkbox"
              checked={isUnknown(UNKNOWN_FIELD.category)}
              onChange={() => toggleUnknown(UNKNOWN_FIELD.category)}
            />
            잘 모르겠어요
          </label>
        ) : null}
      </div>

      {/*
        아래 다섯 축은 **수정 화면에만 있다** — FE 등록 폼이 안 묻고, 등록 뒤 AI 후처리가
        비어 있는 축만 채운다(`problem-post-process`). 등록에 되살리려면 FE부터다(머리말).
      */}
      {isEdit ? (
        <>
          <div className="field">
            <label>어떤 도움이 필요한가요?</label>
            <p className="hint">최대 3개까지 고를 수 있습니다.</p>
            <div className="chips">
              {resources.data?.map((h) => (
                <button
                  key={h.code}
                  type="button"
                  className={v.neededResourceCodes.includes(h.code) ? 'opt on' : 'opt'}
                  onClick={() =>
                    setV((p) => ({
                      ...p,
                      neededResourceCodes: toggle(p.neededResourceCodes, h.code, 3),
                    }))
                  }
                  disabled={isUnknown(UNKNOWN_FIELD.neededResourceCodes)}
                  aria-pressed={v.neededResourceCodes.includes(h.code)}
                >
                  {h.label}
                </button>
              ))}
            </div>
            <label className="unknown-toggle">
              <input
                type="checkbox"
                checked={isUnknown(UNKNOWN_FIELD.neededResourceCodes)}
                onChange={() => toggleUnknown(UNKNOWN_FIELD.neededResourceCodes)}
              />
              잘 모르겠어요
            </label>
          </div>

          <div className="field">
            <label>누구까지 영향을 받나요?</label>
            <div className="chips">
              {IMPACT_SCOPES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={v.impactScope === s.value ? 'opt on' : 'opt'}
                  onClick={() => patch({ impactScope: v.impactScope === s.value ? '' : s.value })}
                  disabled={isUnknown(UNKNOWN_FIELD.impactScope)}
                  aria-pressed={v.impactScope === s.value}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <label className="unknown-toggle">
              <input
                type="checkbox"
                checked={isUnknown(UNKNOWN_FIELD.impactScope)}
                onChange={() => toggleUnknown(UNKNOWN_FIELD.impactScope)}
              />
              잘 모르겠어요
            </label>
          </div>

          <div className="field">
            <label>언제까지 해결되면 좋을까요?</label>
            <div className="chips">
              {DESIRED_PERIODS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  className={v.desiredPeriod === d.value ? 'opt on' : 'opt'}
                  onClick={() => patch({ desiredPeriod: v.desiredPeriod === d.value ? '' : d.value })}
                  disabled={isUnknown(UNKNOWN_FIELD.desiredPeriod)}
                  aria-pressed={v.desiredPeriod === d.value}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <label className="unknown-toggle">
              <input
                type="checkbox"
                checked={isUnknown(UNKNOWN_FIELD.desiredPeriod)}
                onChange={() => toggleUnknown(UNKNOWN_FIELD.desiredPeriod)}
              />
              잘 모르겠어요
            </label>
          </div>

          <div className="field">
            <label htmlFor="barrierNote">지금까지 무엇이 막혀 있었나요?</label>
            <textarea
              id="barrierNote"
              value={v.barrierNote}
              onChange={(e) => patch({ barrierNote: e.target.value })}
              maxLength={1000}
              disabled={isUnknown(UNKNOWN_FIELD.barrier)}
              style={{ minHeight: 80 }}
            />
            <label className="unknown-toggle">
              <input
                type="checkbox"
                checked={isUnknown(UNKNOWN_FIELD.barrier)}
                onChange={() => toggleUnknown(UNKNOWN_FIELD.barrier)}
              />
              잘 모르겠어요
            </label>
          </div>

          <div className="field">
            <label htmlFor="neededResourceNote">해결하려면 무엇이 있어야 할까요?</label>
            <textarea
              id="neededResourceNote"
              value={v.neededResourceNote}
              onChange={(e) => patch({ neededResourceNote: e.target.value })}
              maxLength={1000}
              disabled={isUnknown(UNKNOWN_FIELD.neededResourceNote)}
              style={{ minHeight: 80 }}
            />
            <label className="unknown-toggle">
              <input
                type="checkbox"
                checked={isUnknown(UNKNOWN_FIELD.neededResourceNote)}
                onChange={() => toggleUnknown(UNKNOWN_FIELD.neededResourceNote)}
              />
              잘 모르겠어요
            </label>
          </div>
        </>
      ) : null}

      {/*
        연락처·동의(C-8). **없으면 서버가 400을 낸다** — 선택 항목이 아니다.
        닿을 수단은 한 번에 하나만 받는다(둘 다 받으면 어디로 연락할지 모호해진다).
      */}
      <div className="field">
        <label htmlFor="contactName">
          성함 <span className="req">*</span>
        </label>
        <p className="hint">운영기관이 연락드릴 때 부를 이름입니다. 공개되지 않습니다.</p>
        <input
          id="contactName"
          type="text"
          value={v.contactName}
          onChange={(e) => patch({ contactName: e.target.value })}
          maxLength={60}
          autoComplete="name"
          required
        />
      </div>

      <div className="field">
        <label htmlFor="contactValue">
          {v.contactChannel === 'phone' ? '휴대폰 번호' : '이메일'} <span className="req">*</span>
        </label>
        <p className="hint">
          연락은 이 수단으로만 드리고, <b>어디에도 공개되지 않습니다.</b>
        </p>
        {v.contactChannel === 'phone' ? (
          <input
            id="contactValue"
            type="tel"
            value={v.contactPhone}
            onChange={(e) => patch({ contactPhone: formatPhone(e.target.value) })}
            placeholder="010-0000-0000"
            autoComplete="tel"
            aria-invalid={!isPhoneValid(v.contactPhone)}
            aria-describedby={!isPhoneValid(v.contactPhone) ? 'contactError' : undefined}
            required
          />
        ) : (
          <input
            id="contactValue"
            type="email"
            value={v.contactEmail}
            onChange={(e) => patch({ contactEmail: e.target.value })}
            placeholder="name@example.com"
            maxLength={200}
            autoComplete="email"
            aria-invalid={!isEmailValid(v.contactEmail)}
            aria-describedby={!isEmailValid(v.contactEmail) ? 'contactError' : undefined}
            required
          />
        )}
        {hasContactError && contactValue.trim() !== '' ? (
          <p className="hint" id="contactError" role="alert">
            {v.contactChannel === 'phone'
              ? '휴대폰 번호 형식을 확인해 주세요.'
              : '이메일 형식을 확인해 주세요.'}
          </p>
        ) : null}
        {/* 수단을 바꿔도 **적어 둔 값은 지우지 않는다** — 되돌아올 수 있어야 한다. */}
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 8 }}
          onClick={() => patch({ contactChannel: v.contactChannel === 'phone' ? 'email' : 'phone' })}
        >
          {v.contactChannel === 'phone' ? '이메일로 받을게요' : '휴대폰으로 받을게요'}
        </button>
      </div>

      <div className="field">
        <label>
          동의 <span className="req">*</span>
        </label>
        {CONSENTS.map((c) => (
          <label key={c.key} className="unknown-toggle" style={{ alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={v.consents.includes(c.key)}
              onChange={() => setV((p) => ({ ...p, consents: toggle(p.consents, c.key) }))}
            />
            <span>
              {c.label}
              <br />
              <span className="hint">{c.desc}</span>
            </span>
          </label>
        ))}
        <p className="hint">만 14세 미만은 보호자 동의를 받은 뒤 등록해 주세요.</p>
      </div>

      {error ? <SubmitError error={error} /> : null}

      <button className="btn" type="submit" disabled={!canSubmit}>
        {isPending ? pendingLabel : submitLabel}
      </button>
      {!canSubmit && !isPending ? (
        <p className="hint" style={{ marginTop: 8 }}>
          불편한 점({RAW_TEXT_MIN}자 이상) · 대상 · 동네 · 성함 · 연락처 · 동의를 채우면 보낼 수
          있습니다.
        </p>
      ) : null}
    </form>
  )
}

/**
 * 제출 실패를 시민에게 돌려준다. **모양이 둘이다** — 400은 필드별 `detail`,
 * **422 `insufficient`는 `reasons`**(충분성 게이트 — CLAUDE.md §4-11)다. `reasons`를
 * 안 읽으면 "잠시 후 다시 시도해 주세요"만 떠서, 무엇을 고쳐야 하는지 알 수 없다.
 *
 * 문구는 서버가 준 것을 그대로 쓴다 — 화면에서 다시 지으면 두 벌이 된다.
 */
export function SubmitError({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null
  const lines =
    api !== null && api.reasons.length > 0
      ? api.reasons.map((r) => ({ key: r.code, text: r.message }))
      : (api?.detail ?? []).map((d) => ({ key: d, text: d }))

  return (
    <div className="notice error" style={{ marginBottom: 16 }} role="alert">
      <b>{api?.status === 422 ? '조금만 더 적어 주세요.' : '보내지 못했습니다.'}</b>
      {lines.length > 0 ? (
        <ul>
          {lines.map((l) => (
            <li key={l.key}>{l.text}</li>
          ))}
        </ul>
      ) : (
        ' 잠시 후 다시 시도해 주세요.'
      )}
    </div>
  )
}

/**
 * 주소 — **카카오 검색이 주 경로, 행정동 드롭다운이 폴백**이다.
 *
 * 검색 결과가 행정동코드를 함께 주므로 고른 자리의 동을 추정하지 않는다. 다만 키가 없는
 * 빌드에서는 검색이 통째로 죽으므로 드롭다운을 남긴다 — 그쪽으로 고르면 주소 글자·좌표
 * 없이 동만 확정된다.
 */
function AddressField({
  address,
  regions,
  search,
  onPick,
  onSelectDong,
}: {
  address: PickedAddress
  regions: { code: string; name: string }[]
  search: KakaoAddressSearch
  onPick: (c: AddressCandidate) => Promise<void>
  onSelectDong: (code: string) => void
}) {
  const [query, setQuery] = useState(address.text)

  return (
    <div className="field">
      <label htmlFor="addressQuery">
        어디에서 겪는 일인가요? <span className="req">*</span>
      </label>
      <p className="hint">
        주소나 장소 이름으로 찾아 주세요. 저장되는 것은 <b>동까지</b>입니다.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <input
          id="addressQuery"
          type="text"
          style={{ flex: 1, minWidth: 0 }}
          value={query}
          placeholder="예) 하안동, 광명로, 광명동굴"
          onChange={(e) => {
            setQuery(e.target.value)
            // 글자를 고치면 앞서 고른 코드를 버린다 — 코드와 글자가 다른 곳을 가리키면 안 된다.
            if (address.regionCode !== '') onSelectDong('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void search.search(query)
            }
          }}
        />
        <button
          type="button"
          className="btn ghost"
          onClick={() => void search.search(query)}
          disabled={search.isLoading}
        >
          주소 찾기
        </button>
      </div>

      {search.isLoading ? (
        <p className="hint" role="status">
          주소를 찾는 중입니다.
        </p>
      ) : null}

      {search.error !== null ? (
        <p className="hint" role="alert">
          {search.error}
        </p>
      ) : null}

      {search.results.length > 0 ? (
        <ul
          aria-label="주소 검색 결과"
          style={{
            listStyle: 'none',
            margin: '8px 0 0',
            padding: 0,
            maxHeight: 232,
            overflowY: 'auto',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
          }}
        >
          {search.results.map((c) => (
            <li key={c.id ?? `${c.jibun}-${c.text}`}>
              <button
                type="button"
                onClick={() => void onPick(c)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 0,
                  borderTop: '1px solid var(--line-soft)',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                {/* 장소로 찾은 줄은 이름을 첫 줄에 둔다 — 주소가 비슷한 장소가 함께 나온다. */}
                {c.placeName !== undefined ? <b>{c.placeName}</b> : null}
                {c.placeName !== undefined ? <br /> : null}
                <span>{c.text}</span>
                {/* 도로명과 지번이 같으면 두 번 적지 않는다. */}
                {c.jibun !== c.text ? (
                  <>
                    <br />
                    <span className="hint">{c.jibun}</span>
                  </>
                ) : null}
                {addressMetaText(c.regionName, c.legalName, c.postalCode) !== '' ? (
                  <>
                    <br />
                    <span className="hint">
                      {addressMetaText(c.regionName, c.legalName, c.postalCode)}
                    </span>
                  </>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* 찾아봤는데 없다 — 아직 안 찾은 것과 다르다. */}
      {search.hasSearched &&
      !search.isLoading &&
      search.error === null &&
      search.results.length === 0 ? (
        <p className="hint" role="alert">
          찾는 주소가 없습니다. 동 이름이나 도로명으로 다시 찾아 주세요.
        </p>
      ) : null}

      {/* 고른 자리를 지도로 한 번 보여 준다 — 고치는 수단이 아니라 확인용이다. */}
      {address.lat !== null && address.lng !== null && address.regionCode !== '' ? (
        <KakaoPointMap lat={address.lat} lng={address.lng} />
      ) : null}

      {address.regionCode !== '' ? (
        <p className="hint">
          <b>{address.matchedName}</b> 으로 접수됩니다.
          {addressMetaText(address.matchedName, address.legalName, address.postalCode) !== '' ? (
            <>
              <br />
              {addressMetaText(address.matchedName, address.legalName, address.postalCode)}
            </>
          ) : null}
        </p>
      ) : null}

      {/* 골랐는데 우리 목록에 없는 동이었다 — 광명시 밖이다. */}
      {address.regionCode === '' && address.matchedName !== '' ? (
        <p className="hint" role="alert">
          광명시 안의 주소만 접수할 수 있습니다.
        </p>
      ) : null}

      <p className="hint" style={{ marginTop: 12 }}>
        주소를 찾기 어려우면 동을 직접 고르세요.
      </p>
      <select
        id="regionCode"
        aria-label="어느 동네인가요?"
        value={address.regionCode}
        onChange={(e) => {
          setQuery('')
          onSelectDong(e.target.value)
        }}
      >
        <option value="">선택해 주세요</option>
        {regions.map((r) => (
          <option key={r.code} value={r.code}>
            {r.name}
          </option>
        ))}
      </select>
    </div>
  )
}
